import React, { useEffect, useReducer, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import WorkflowUploader from "../../../components/workflow-runner/WorkflowUploader";
import WorkflowPreviewCard from "../../../components/workflow-runner/WorkflowPreviewCard";
import StageProgressBar from "../../../components/workflow-runner/StageProgressBar";
import StageExecutionView from "../../../components/workflow-runner/StageExecutionView";
import CompletedStageDrawer from "../../../components/workflow-runner/CompletedStageDrawer";
import WorkflowCompletionScreen from "../../../components/workflow-runner/WorkflowCompletionScreen";
import { loadWorkflow, clearWorkflow } from "../../../utils/executionStorage";
import { executionReducer, ACTIONS } from "../../../utils/executionReducer";
import "./WorkflowRunner.css";
import api from "../../../api/client";

function toBackendStatus(status) {
  if (status === "completed") return "COMPLETED";
  if (status === "in_progress") return "IN_PROGRESS";
  return "IN_PROGRESS";
}

export default function WorkflowRunner() {
  const { runId } = useParams();
  const isDbRunMode = !!runId;

  const [workflow, setWorkflow] = useState(isDbRunMode ? null : loadWorkflow());
  const [executionState, dispatch] = useReducer(executionReducer, null);
  const [drawerEntry, setDrawerEntry] = useState(null);
  const [loading, setLoading] = useState(isDbRunMode);
  const [error, setError] = useState(null);
  const syncSkipRef = useRef(true);

  // DB mode: load run by ID
  useEffect(() => {
    if (!isDbRunMode) return;
    const loadRun = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.get(`/workflow-runs/${runId}`);
        const run = resp.data;
        const wf = run.executionState?.workflowDefinition ||
          run.workflow?.definition || {
            workflowId: run.workflowId,
            name: run.workflow?.name || "Workflow",
            stages: [],
            connections: [],
          };
        setWorkflow({
          ...wf,
          workflowId: wf.workflowId || run.workflowId,
          name: wf.name || run.workflow?.name || "Workflow",
        });
        dispatch({
          type: ACTIONS.HYDRATE,
          payload: run.executionState || null,
        });
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load workflow run");
      } finally {
        setLoading(false);
        syncSkipRef.current = true;
      }
    };
    loadRun();
  }, [api, isDbRunMode, runId]);

  // legacy mode starts clean with no persisted execution state
  useEffect(() => {
    if (isDbRunMode) return;
    dispatch({ type: ACTIONS.HYDRATE, payload: null });
  }, [isDbRunMode]);

  // Persist transitions to DB in run mode
  useEffect(() => {
    if (!isDbRunMode || !executionState) return;
    if (syncSkipRef.current) {
      syncSkipRef.current = false;
      return;
    }
    const persist = async () => {
      try {
        await api.put(`/workflow-runs/${runId}`, {
          executionState,
          currentStageId:
            executionState.status === "completed"
              ? null
              : executionState.currentStageId,
          status: toBackendStatus(executionState.status),
        });
      } catch (err) {
        console.error("Failed to persist workflow run", err);
      }
    };
    persist();
  }, [api, runId, executionState, isDbRunMode]);

  const handleUploaded = (wf) => {
    setWorkflow(wf);
    dispatch({ type: ACTIONS.HYDRATE, payload: null });
  };

  const handleStart = (startStageId) => {
    dispatch({ type: ACTIONS.START, payload: { workflow, startStageId } });
  };

  const handleStageClick = (stageId) => {
    if (!executionState || !workflow) return;
    const entry = [...executionState.history]
      .reverse()
      .find((item) => item.stageId === stageId && item.completedAt);
    if (entry) {
      const stage = workflow.stages.find((s) => s.id === stageId);
      setDrawerEntry({ entry, stage });
    }
  };

  const handleClearWorkflow = () => {
    if (isDbRunMode) return;
    clearWorkflow();
    setWorkflow(null);
    setDrawerEntry(null);
    dispatch({ type: ACTIONS.RESET });
  };

  const handleRestart = () => {
    if (isDbRunMode) return;
    dispatch({ type: ACTIONS.RESET });
  };

  const inProgress =
    workflow &&
    executionState &&
    executionState.status === "in_progress" &&
    executionState.currentStageId;

  if (loading) {
    return <div className="workflow-runner-page">Loading workflow run...</div>;
  }

  if (error) {
    return <div className="workflow-runner-page error-message">{error}</div>;
  }

  return (
    <div className="workflow-runner-page">
      {!workflow && !isDbRunMode && (
        <WorkflowUploader onUploaded={handleUploaded} />
      )}

      {workflow && !isDbRunMode && (
        <div className="workflow-runner-actions">
          <button className="btn-secondary" onClick={handleClearWorkflow}>
            Clear Workflow & Start New
          </button>
        </div>
      )}

      {workflow &&
        !inProgress &&
        !(executionState && executionState.status === "completed") &&
        !isDbRunMode && (
          <div>
            <WorkflowPreviewCard workflow={workflow} onStart={handleStart} />
          </div>
        )}

      {workflow && inProgress && (
        <div>
          <StageProgressBar
            workflow={workflow}
            executionState={executionState}
            onStageClick={handleStageClick}
          />
          <StageExecutionView
            workflow={workflow}
            executionState={executionState}
            dispatch={dispatch}
          />
          {drawerEntry && (
            <CompletedStageDrawer
              entry={drawerEntry.entry}
              stage={drawerEntry.stage}
              onClose={() => setDrawerEntry(null)}
            />
          )}
        </div>
      )}

      {workflow && executionState && executionState.status === "completed" && (
        <WorkflowCompletionScreen
          state={executionState}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}
